import { cn } from '@/lib/cn';

interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular' | 'rounded';
  width?: string | number;
  height?: string | number;
  lines?: number;
  animate?: boolean;
}

/**
 * Skeleton - Loading placeholder with shimmer animation
 * 
 * Features:
 * - Multiple variants (text, circular, rectangular, rounded)
 * - Shimmer animation
 * - Customizable dimensions
 * - Multi-line text skeleton
 */
export function Skeleton({
  className,
  variant = 'rectangular',
  width,
  height,
  lines = 1,
  animate = true,
}: SkeletonProps) {
  const variantStyles = {
    text: 'rounded h-4',
    circular: 'rounded-full',
    rectangular: 'rounded-none',
    rounded: 'rounded-xl',
  };

  const baseStyles = cn(
    'bg-navy-700/50 relative overflow-hidden',
    variantStyles[variant],
    animate && 'shimmer',
    className
  );

  const style = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
  };

  // Multi-line text skeleton
  if (variant === 'text' && lines > 1) {
    return (
      <div className="space-y-3">
        {Array.from({ length: lines }).map((_, i) => (
          <div
            key={i}
            className={cn(baseStyles, i === lines - 1 && 'w-4/5')}
            style={{ height: height || '16px' }}
          />
        ))}
      </div>
    );
  }

  return <div className={baseStyles} style={style} />;
}

/**
 * SkeletonCard - Pre-built card skeleton
 */
export function SkeletonCard({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl p-6',
        className
      )}
    >
      <div className="flex items-center gap-4 mb-4">
        <Skeleton variant="circular" width={48} height={48} />
        <div className="flex-1 space-y-2">
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="40%" />
        </div>
      </div>
      <Skeleton variant="text" lines={3} />
    </div>
  );
}

/**
 * SkeletonAvatar - Pre-built avatar skeleton
 */
export function SkeletonAvatar({ 
  size = 'md' 
}: { 
  size?: 'sm' | 'md' | 'lg' 
}) {
  const sizes = {
    sm: 32,
    md: 40,
    lg: 56,
  };

  return (
    <Skeleton 
      variant="circular" 
      width={sizes[size]} 
      height={sizes[size]} 
    />
  );
}

/**
 * SkeletonButton - Pre-built button skeleton
 */
export function SkeletonButton({
  size = 'md',
}: {
  size?: 'sm' | 'md' | 'lg';
}) {
  const sizes = {
    sm: { width: 80, height: 32 },
    md: { width: 100, height: 40 },
    lg: { width: 120, height: 48 },
  };

  return (
    <Skeleton
      variant="rounded"
      width={sizes[size].width}
      height={sizes[size].height}
    />
  );
}

/**
 * SkeletonTable - Pre-built table skeleton
 */
export function SkeletonTable({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex gap-4 pb-3 border-b border-white/5">
        <Skeleton variant="text" width="20%" />
        <Skeleton variant="text" width="30%" />
        <Skeleton variant="text" width="25%" />
        <Skeleton variant="text" width="15%" />
      </div>
      
      {/* Rows */}
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex gap-4 py-2">
          <Skeleton variant="text" width="20%" />
          <Skeleton variant="text" width="30%" />
          <Skeleton variant="text" width="25%" />
          <Skeleton variant="text" width="15%" />
        </div>
      ))}
    </div>
  );
}

export default Skeleton;

